import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Loader, ActionIcon } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { createB2BAPI } from "./api/Interceptor";
import NotificationCard from "./components/website/NotificationCard";
import "./components/website/NotificationCard.css";
import { IconArrowLeft } from "@tabler/icons-react";
import "./AllNotifications.css";

const AllNotifications = () => {
  const navigate = useNavigate();
  const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchNotifications = useCallback(async (pageNum = 0, refresh = false) => {
    try {
      if (pageNum === 0) setLoading(true);
      else setIsMoreLoading(true);

      const res = await B2B_API.get(`notification/all?page=${pageNum}&size=20`).json();
      const data = res?.response?.content || [];
      
      if (refresh) {
        setNotifications(data);
      } else {
        setNotifications((prev) => [...prev, ...data]);
      }
      
      setTotalPages(res?.response?.totalPages || 0);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
      setIsMoreLoading(false);
    }
  }, [B2B_API]);

  // Poll unread count every 30s; if count changes, refresh notifications
  const prevUnreadRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await B2B_API.get(`notification/unread-count`).json();
      const newCount = res?.response?.unreadCount ?? 0;

      if (prevUnreadRef.current !== null && newCount !== prevUnreadRef.current) {
        setPage(0);
        fetchNotifications(0, true);
      }
      prevUnreadRef.current = newCount;
    } catch (error) {
      console.error("Error polling unread count:", error);
    }
  }, [B2B_API, fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();

    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchUnreadCount();
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchNotifications, fetchUnreadCount]);

  const markAsRead = async (id) => {
    try {
      await B2B_API.post(`notification/mark-read/${id}`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await B2B_API.post('notification/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  const handleNotificationClick = (item) => {
    if (!item.isRead) {
      markAsRead(item.id);
    }

    if (["NEW", "CONFIRMED"].includes(item.screen)) {
      navigate(`/order-Details?orderNo=${item.orderNo}`);
    } else {
      const receiptNo = item.referenceId || item.orderNo;
      navigate(`/order-Details?receiptNo=${receiptNo}`);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && !isMoreLoading && page + 1 < totalPages) {
      fetchNotifications(page + 1);
    }
  };

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <div className="header-left">
          <ActionIcon 
            variant="subtle" 
            onClick={() => navigate(-1)}
            className="back-btn"
          >
            <IconArrowLeft size={24} />
          </ActionIcon>
          <h1>Notifications</h1>
        </div>
        <Button 
          variant="subtle" 
          color="#8BC53F" 
          onClick={markAllAsRead}
          className="mark-all-btn-screen"
        >
          Mark all as read
        </Button>
      </div>

      <div 
        className="notifications-list-screen"
        onScroll={handleScroll}
        style={{ overflowY: "auto" }}
      >
        {loading && page === 0 ? (
          <div className="loading-container">
                   <Loader color={"var(--primary)"} type="oval" />
                 </div>
        ) : notifications.length > 0 ? (
          <>
            {notifications.map((item) => (
              <NotificationCard 
                key={item.id} 
                item={item} 
                onClick={handleNotificationClick} 
              />
            ))}
            
            {isMoreLoading && (
              <div className="loading-container">
                       <Loader color={"var(--primary)"} type="oval" />
                     </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>No notifications yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllNotifications;